import type { TFunction } from 'i18next';
import type { FieldErrors } from 'react-hook-form';
import * as Yup from 'yup';
import {
  getCategoryId,
  materialLinesFromApi,
  materialLinesToApiPayload,
  resolveWarrantyType,
  sanitizeProcedureSteps,
  type ProcedureStep,
  type TreatmentCatalogFormData,
  type TreatmentCatalogFormErrors,
  type TreatmentCatalogItem,
} from '../types/treatmentCatalog.types';

export type CatalogFormTab = 'general' | 'steps' | 'materials';

/** Matches TreatmentCatalog mongoose maxlength. */
export const CATALOG_NAME_MAX_LENGTH = 150;
export const CATALOG_DESCRIPTION_MAX_LENGTH = 500;
export const CATALOG_STEP_TITLE_MAX_LENGTH = 100;

/** Matches TreatmentCatalog mongoose min/max. */
export const CATALOG_PRICE_MIN = 0;
export const CATALOG_PRICE_MAX = 999_999_999;
export const CATALOG_DURATION_MIN_MINUTES = 0;
export const CATALOG_DURATION_MAX_HOURS = 6;
export const CATALOG_DURATION_MAX_MINUTES = CATALOG_DURATION_MAX_HOURS * 60;

export const createEmptyTreatmentCatalogForm = (): TreatmentCatalogFormData => ({
  name: '',
  category: '',
  description: '',
  price: '',
  duration: '',
  uom: '',
  labOrderEnabled: false,
  warrantyType: '',
  warrantyMonths: '',
  defaultLabWorkType: '',
  defaultLabMaterial: '',
  countsTowardKpi: true,
  notes: '',
  isActive: true,
  procedureSteps: [],
  materialLines: [],
});

type Translate = TFunction<['treatment', 'common']>;

const yupErrorToCatalogErrors = (error: Yup.ValidationError): TreatmentCatalogFormErrors => {
  const errors: TreatmentCatalogFormErrors = {};
  if (error.inner.length > 0) {
    for (const inner of error.inner) {
      const path = inner.path;
      if (!path || errors[path as keyof TreatmentCatalogFormErrors]) continue;
      errors[path as keyof TreatmentCatalogFormErrors] = inner.message;
    }
    return errors;
  }
  if (error.path) {
    errors[error.path as keyof TreatmentCatalogFormErrors] = error.message;
  }
  return errors;
};

export const buildTreatmentCatalogFormSchema = (
  t: TFunction<['treatment', 'common']>
): Yup.ObjectSchema<TreatmentCatalogFormData> =>
  Yup.object({
    name: Yup.string()
      .trim()
      .max(CATALOG_NAME_MAX_LENGTH)
      .required(t('validation.nameRequired')),
    category: Yup.string().required(t('validation.categoryRequired')),
    description: Yup.string().max(CATALOG_DESCRIPTION_MAX_LENGTH),
    price: Yup.string()
      .required(t('validation.priceRequired'))
      .test('price-range', function priceRange(value) {
        if (value === undefined || value.trim() === '') {
          return this.createError({ message: t('validation.priceRequired') });
        }
        const price = Number(value);
        if (!Number.isFinite(price) || price < CATALOG_PRICE_MIN) {
          return this.createError({ message: t('validation.pricePositive') });
        }
        if (price > CATALOG_PRICE_MAX) {
          return this.createError({
            message: t('validation.priceMax', { max: CATALOG_PRICE_MAX.toLocaleString('vi-VN') }),
          });
        }
        return true;
      }),
    duration: Yup.string()
      .required(t('validation.durationRequired'))
      .test('duration-range', t('validation.durationPositive'), function durationRange(value) {
        if (value === undefined || value.trim() === '') {
          return this.createError({ message: t('validation.durationRequired') });
        }
        const duration = Number(value);
        if (!Number.isFinite(duration) || duration < CATALOG_DURATION_MIN_MINUTES) {
          return this.createError({ message: t('validation.durationPositive') });
        }
        if (duration > CATALOG_DURATION_MAX_MINUTES) {
          return this.createError({
            message: t('validation.durationMax', { hours: CATALOG_DURATION_MAX_HOURS }),
          });
        }
        return true;
      }),
    uom: Yup.string(),
    labOrderEnabled: Yup.boolean(),
    warrantyType: Yup.string().oneOf(['', 'months', 'lifetime']),
    warrantyMonths: Yup.string().when('warrantyType', {
      is: 'months',
      then: (schema) =>
        schema
          .required(t('validation.warrantyMonthsRequired'))
          .test('warranty-months', t('validation.warrantyMonthsPositive'), (value) => {
            const months = Number(value);
            return Boolean(value) && Number.isInteger(months) && months >= 1;
          }),
      otherwise: (schema) => schema,
    }),
    defaultLabWorkType: Yup.string(),
    defaultLabMaterial: Yup.string(),
    countsTowardKpi: Yup.boolean(),
    notes: Yup.string(),
    isActive: Yup.boolean(),
    procedureSteps: Yup.array()
      .default([])
      .test('procedure-steps', function procedureStepsValid(steps) {
        const rows = steps ?? [];
        const hasIncompleteStep = rows.some(
          (step) =>
            !step.title.trim() &&
            (step.description?.trim() || step.durationMinutes !== undefined)
        );
        if (hasIncompleteStep) {
          return this.createError({
            message: t('validation.procedureStepTitleRequired'),
            path: 'procedureSteps',
          });
        }
        const hasInvalidDuration = rows.some(
          (step) =>
            step.durationMinutes !== undefined &&
            (Number.isNaN(step.durationMinutes) || step.durationMinutes < 0)
        );
        if (hasInvalidDuration) {
          return this.createError({
            message: t('validation.procedureStepDurationInvalid'),
            path: 'procedureSteps',
          });
        }
        return true;
      }),
    materialLines: Yup.array().default([]),
  }) as Yup.ObjectSchema<TreatmentCatalogFormData>;

export const validateTreatmentCatalogForm = (
  formData: TreatmentCatalogFormData,
  t: Translate
): TreatmentCatalogFormErrors => {
  try {
    buildTreatmentCatalogFormSchema(t).validateSync(formData, { abortEarly: false });
    return {};
  } catch (err) {
    if (err instanceof Yup.ValidationError) return yupErrorToCatalogErrors(err);
    return {};
  }
};

const fieldErrorMessage = (value: unknown): string | undefined => {
  if (!value) return undefined;
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && value !== null && 'message' in value) {
    const message = (value as { message?: unknown }).message;
    return typeof message === 'string' ? message : undefined;
  }
  return undefined;
};

export const catalogFormTabForFieldErrors = (
  errors: FieldErrors<TreatmentCatalogFormData>,
  options?: { skipSteps?: boolean }
): CatalogFormTab => {
  const mapped: TreatmentCatalogFormErrors = {
    name: fieldErrorMessage(errors.name),
    category: fieldErrorMessage(errors.category),
    price: fieldErrorMessage(errors.price),
    duration: fieldErrorMessage(errors.duration),
    uom: fieldErrorMessage(errors.uom),
    warrantyMonths: fieldErrorMessage(errors.warrantyMonths),
    procedureSteps: fieldErrorMessage(errors.procedureSteps),
    materialLines: fieldErrorMessage(errors.materialLines),
  };
  return catalogFormTabForErrors(mapped, options);
};

export const catalogFormTabForErrors = (
  errors: TreatmentCatalogFormErrors,
  options?: { skipSteps?: boolean }
): CatalogFormTab => {
  const hasGeneral =
    Boolean(errors.name) ||
    Boolean(errors.category) ||
    Boolean(errors.price) ||
    Boolean(errors.duration) ||
    Boolean(errors.uom) ||
    Boolean(errors.warrantyMonths);
  if (hasGeneral) return 'general';
  if (errors.procedureSteps && !options?.skipSteps) return 'steps';
  if (errors.materialLines) return 'materials';
  return 'general';
};

export const mapCatalogItemToForm = (item: TreatmentCatalogItem): TreatmentCatalogFormData => {
  const warrantyType = resolveWarrantyType(item.warrantyType);
  return {
    name: item.name || '',
    category: getCategoryId(item.category),
    description: item.description || '',
    price: item.price == null ? '' : String(item.price),
    duration: item.duration == null ? '' : String(item.duration),
    uom: item.uom || '',
    labOrderEnabled: Boolean(item.labOrderEnabled),
    warrantyType: warrantyType ?? '',
    warrantyMonths: warrantyType === 'months' && item.warrantyMonths ? String(item.warrantyMonths) : '',
    defaultLabWorkType: item.defaultLabWorkType || '',
    defaultLabMaterial: item.defaultLabMaterial || '',
    countsTowardKpi: item.countsTowardKpi !== false,
    notes: item.notes || '',
    isActive: item.isActive !== undefined ? item.isActive : true,
    procedureSteps: (item.procedureSteps ?? []).map((step) => ({
      ...step,
      countsTowardKpi: step.countsTowardKpi !== false,
    })),
    materialLines: materialLinesFromApi(item.materialLines),
  };
};

/** Snapshot procedure steps from another catalog item into the open form. */
export const copyProcedureStepsFromCatalog = (
  item: Pick<TreatmentCatalogItem, 'procedureSteps'>
): { procedureSteps: ProcedureStep[] } => {
  const procedureSteps = sanitizeProcedureSteps(
    (item.procedureSteps ?? []).map((step) => ({
      ...step,
      title: step.title ?? '',
      countsTowardKpi: step.countsTowardKpi !== false,
    }))
  );
  return { procedureSteps };
};

const toCatalogPayload = (
  formData: TreatmentCatalogFormData,
  emptyLabAndWarranty: undefined | null
): Partial<TreatmentCatalogItem> => {
  const procedureSteps = sanitizeProcedureSteps(formData.procedureSteps);
  return {
    ...formData,
    price: Number(formData.price),
    duration: Number(formData.duration),
    procedureSteps,
    countsTowardKpi: formData.countsTowardKpi !== false,
    materialLines: materialLinesToApiPayload(formData.materialLines) as unknown as TreatmentCatalogItem['materialLines'],
    warrantyType: formData.warrantyType || emptyLabAndWarranty,
    warrantyMonths:
      formData.warrantyType === 'months' ? Number(formData.warrantyMonths) : emptyLabAndWarranty,
    defaultLabWorkType: formData.labOrderEnabled
      ? formData.defaultLabWorkType || emptyLabAndWarranty
      : emptyLabAndWarranty,
    defaultLabMaterial: formData.labOrderEnabled
      ? formData.defaultLabMaterial || emptyLabAndWarranty
      : emptyLabAndWarranty,
  } as Partial<TreatmentCatalogItem>;
};

export const toCreateCatalogPayload = (
  formData: TreatmentCatalogFormData
): Partial<TreatmentCatalogItem> => toCatalogPayload(formData, undefined);

/** Update must send null so the API unsets warranty / lab defaults. */
export const toUpdateCatalogPayload = (
  formData: TreatmentCatalogFormData
): Partial<TreatmentCatalogItem> => toCatalogPayload(formData, null);
